// US Soccer Executive PDF Template
// Strategic Document for Federation Leadership, Commercial Directors, Board Review

export const generateExecutiveUSSoccerPDF = (data) => {
  const { usmnt, uswnt, verifiedSocialData, isDemoMode } = data;
  
  const usmntBrand = usmnt?.brand_metrics || {};
  const usmntDigital = usmnt?.digital_presence || {};
  const usmntEco = usmnt?.economic_value || {};
  const usmntCycle = usmnt?.current_cycle || {};
  const usmntHistorical = usmnt?.historical_cycles || [];
  const usmntRoster = usmnt?.roster_brand_signals || {};
  
  const uswntBrand = uswnt?.brand_metrics || {};
  const uswntDigital = uswnt?.digital_presence || {};
  const uswntEco = uswnt?.economic_value || {};
  const uswntCycle = uswnt?.current_cycle || {};
  const uswntHistorical = uswnt?.historical_cycles || [];
  const uswntRoster = uswnt?.roster_brand_signals || {};
  
  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(0)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  };
  
  const formatNumber = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value?.toString() || '0';
  };
  
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  
  // Get verified social data with source tracking
  const getVerifiedValue = (teamId, platform) => {
    if (isDemoMode) {
      const team = teamId === 'usmnt' ? usmnt : uswnt;
      const digital = team?.digital_presence || {};
      return { value: digital[platform]?.followers || digital[platform]?.subscribers || 0, source: 'DEMONSTRATION' };
    }
    const teamData = verifiedSocialData?.teams?.[teamId]?.platforms?.[platform];
    if (teamData) {
      return { 
        value: teamData.followers || teamData.subscribers || 0, 
        source: teamData.source || 'ESTIMATED',
        handle: teamData.handle 
      };
    }
    const team = teamId === 'usmnt' ? usmnt : uswnt;
    const digital = team?.digital_presence || {};
    return { value: digital[platform]?.followers || digital[platform]?.subscribers || 0, source: 'DEMONSTRATION' };
  };
  
  const lastUpdated = verifiedSocialData?.teams?.usmnt?.last_updated;
  const formattedLastUpdated = lastUpdated ? new Date(lastUpdated).toLocaleDateString('en-US', { 
    year: 'numeric', month: 'short', day: 'numeric' 
  }) : null;
  
  // Generate evolution data
  const generateEvolutionData = (historicalCycles, currentScore) => {
    const rows = [];
    if (historicalCycles && historicalCycles.length > 0) {
      historicalCycles.forEach(cycle => {
        const startYear = parseInt(cycle.years.split('-')[0]);
        const endYear = parseInt(cycle.years.split('-')[1]);
        rows.push({ year: startYear, score: cycle.brand_index_start, event: 'Cycle Start' });
        rows.push({ year: endYear, score: cycle.brand_index_end, event: cycle.world_cup_result });
      });
    }
    rows.push({ year: 2026, score: currentScore, event: 'Current Assessment' });
    return rows.sort((a, b) => a.year - b.year);
  };
  
  const usmntEvolution = generateEvolutionData(usmntHistorical, usmntBrand.overall_brand_score);
  const uswntEvolution = generateEvolutionData(uswntHistorical, uswntBrand.overall_brand_score);
  
  // Generate SVG chart
  const generateEvolutionSVG = (evolutionData, teamColor, teamName) => {
    const width = 480;
    const height = 180;
    const padding = { top: 25, right: 45, bottom: 35, left: 45 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;
    
    const years = evolutionData.map(d => d.year);
    const scores = evolutionData.map(d => d.score);
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);
    const minScore = Math.floor((Math.min(...scores) - 10) / 10) * 10;
    const maxScore = Math.ceil((Math.max(...scores) + 10) / 10) * 10;
    
    const scaleX = (year) => padding.left + ((year - minYear) / (maxYear - minYear || 1)) * chartWidth;
    const scaleY = (score) => padding.top + chartHeight - ((score - minScore) / (maxScore - minScore || 1)) * chartHeight;
    
    const pathD = evolutionData.map((d, i) => 
      `${i === 0 ? 'M' : 'L'} ${scaleX(d.year)} ${scaleY(d.score)}`
    ).join(' ');
    
    const yTicks = [];
    for (let s = minScore; s <= maxScore; s += 10) yTicks.push(s);
    
    // Calculate YoY change for annotation
    const lastTwo = evolutionData.slice(-2);
    const yoyChange = lastTwo.length >= 2 ? lastTwo[1].score - lastTwo[0].score : 0;
    const yoyPct = lastTwo.length >= 2 && lastTwo[0].score > 0 
      ? ((lastTwo[1].score - lastTwo[0].score) / lastTwo[0].score * 100).toFixed(1)
      : 0;
    
    return `
      <svg viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" style="width: 100%; height: auto;">
        <defs>
          <linearGradient id="areaGrad${teamName}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="${teamColor}" stop-opacity="0.15"/>
            <stop offset="100%" stop-color="${teamColor}" stop-opacity="0.02"/>
          </linearGradient>
        </defs>
        
        <!-- Grid lines -->
        ${yTicks.map(s => `
          <line x1="${padding.left}" y1="${scaleY(s)}" x2="${width - padding.right}" y2="${scaleY(s)}" 
                stroke="#e5e7eb" stroke-dasharray="4,4" />
          <text x="${padding.left - 10}" y="${scaleY(s) + 4}" font-size="9" fill="#6b7280" text-anchor="end" font-family="system-ui">${s}</text>
        `).join('')}
        
        <!-- X-axis labels -->
        ${evolutionData.map(d => `
          <text x="${scaleX(d.year)}" y="${height - padding.bottom + 16}" font-size="9" fill="#6b7280" text-anchor="middle" font-family="system-ui">${d.year}</text>
        `).join('')}
        
        <!-- Area fill -->
        <path d="${pathD} L ${scaleX(evolutionData[evolutionData.length-1].year)} ${padding.top + chartHeight} L ${scaleX(evolutionData[0].year)} ${padding.top + chartHeight} Z" 
              fill="url(#areaGrad${teamName})" />
        
        <!-- Main line -->
        <path d="${pathD}" fill="none" stroke="${teamColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
        
        <!-- Data points -->
        ${evolutionData.map((d, i) => {
          const isLast = i === evolutionData.length - 1;
          return `
            <circle cx="${scaleX(d.year)}" cy="${scaleY(d.score)}" r="${isLast ? 5 : 3.5}" fill="${isLast ? teamColor : 'white'}" stroke="${teamColor}" stroke-width="2" />
            ${isLast ? `
              <rect x="${scaleX(d.year) - 16}" y="${scaleY(d.score) - 26}" width="32" height="18" rx="3" fill="${teamColor}" />
              <text x="${scaleX(d.year)}" y="${scaleY(d.score) - 13}" font-size="10" fill="white" text-anchor="middle" font-weight="600" font-family="system-ui">${d.score}</text>
            ` : ''}
          `;
        }).join('')}
        
        <!-- YoY annotation -->
        <text x="${width - padding.right}" y="${padding.top - 8}" font-size="9" fill="${yoyChange >= 0 ? '#059669' : '#dc2626'}" text-anchor="end" font-family="system-ui">
          ${yoyChange >= 0 ? '+' : ''}${yoyPct}% YoY
        </text>
        
        <!-- Y-axis label -->
        <text x="10" y="${height/2}" font-size="8" fill="#9ca3af" text-anchor="middle" transform="rotate(-90, 10, ${height/2})" font-family="system-ui">Brand Index Score</text>
      </svg>
    `;
  };
  
  // Combined values
  const combinedLow = (usmntEco.range_low || 240000000) + (uswntEco.range_low || 165000000);
  const combinedHigh = (usmntEco.range_high || 340000000) + (uswntEco.range_high || 230000000);
  const usmntYoY = usmntEco.year_over_year_change || 18.5;
  const uswntYoY = uswntEco.year_over_year_change || 12.2;

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <title>US Soccer Federation — Strategic Brand Intelligence Assessment</title>
      <style>
        @page { 
          size: A4; 
          margin: 0.9in 0.8in;
        }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
          font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', Arial, sans-serif;
          color: #1f2937;
          line-height: 1.65;
          font-size: 10px;
          background: white;
          position: relative;
        }
        .pdf-watermark {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(-35deg);
          font-size: 48px;
          font-weight: 800;
          color: rgba(220, 38, 38, 0.06);
          white-space: nowrap;
          pointer-events: none;
          z-index: 0;
          letter-spacing: 4px;
        }
        .pdf-watermark-footer {
          text-align: center;
          font-size: 9px;
          color: #999;
          border-top: 1px solid #e5e7eb;
          padding-top: 12px;
          margin-top: 30px;
        }
        
        /* === COVER === */
        .cover {
          height: 100vh;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          text-align: center;
          page-break-after: always;
          background: linear-gradient(145deg, #111827 0%, #1f2937 50%, #111827 100%);
          color: white;
          margin: -0.9in -0.8in;
          padding: 2.5in 1.5in;
          position: relative;
        }
        .cover-logo { font-size: 16px; font-weight: 600; letter-spacing: 4px; color: #10b981; margin-bottom: 80px; }
        .cover-title { font-size: 30px; font-weight: 700; margin-bottom: 12px; line-height: 1.25; letter-spacing: -0.5px; }
        .cover-subtitle { font-size: 14px; color: #9ca3af; margin-bottom: 70px; font-weight: 400; }
        .cover-badge { 
          display: inline-block; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15);
          padding: 10px 28px; border-radius: 4px; font-size: 10px; letter-spacing: 3px; font-weight: 600;
        }
        .cover-meta { margin-top: 60px; font-size: 10px; color: #6b7280; }
        .cover-footer { position: absolute; bottom: 50px; font-size: 9px; color: #4b5563; }
        
        /* === PAGES === */
        .page { page-break-after: always; padding: 0; }
        .page:last-child { page-break-after: auto; }
        
        .page-header {
          display: flex; justify-content: space-between; align-items: flex-end;
          border-bottom: 1.5px solid #1f2937; padding-bottom: 10px; margin-bottom: 28px;
        }
        .page-logo { font-size: 12px; font-weight: 700; color: #10b981; letter-spacing: 1px; }
        .page-logo span { color: #1f2937; }
        .page-title { font-size: 9px; color: #6b7280; text-transform: uppercase; letter-spacing: 1.5px; font-weight: 500; }
        
        .page-footer {
          margin-top: 35px; padding-top: 12px; border-top: 1px solid #e5e7eb;
          display: flex; justify-content: space-between; font-size: 8px; color: #9ca3af;
        }
        
        /* === SECTIONS === */
        .section { margin-bottom: 28px; }
        .section-title {
          font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px;
          color: #1f2937; margin-bottom: 16px; padding-bottom: 8px; border-bottom: 1px solid #e5e7eb;
        }
        .section-intro { font-size: 10px; color: #4b5563; margin-bottom: 16px; line-height: 1.7; }
        
        /* === CARDS === */
        .team-card {
          background: #f9fafb; border-radius: 6px; padding: 20px; border-left: 3px solid #10b981;
        }
        .team-card.secondary { border-left-color: #3b82f6; }
        .team-name { font-size: 13px; font-weight: 700; color: #1f2937; margin-bottom: 2px; }
        .team-meta { font-size: 9px; color: #6b7280; margin-bottom: 14px; }
        .team-value { font-size: 20px; font-weight: 700; color: #10b981; margin-bottom: 2px; }
        .team-card.secondary .team-value { color: #3b82f6; }
        .team-label { font-size: 8px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.5px; }
        
        .metric-box { background: #f9fafb; padding: 14px; border-radius: 5px; text-align: center; }
        .metric-value { font-size: 18px; font-weight: 700; color: #1f2937; }
        .metric-label { font-size: 8px; color: #6b7280; text-transform: uppercase; margin-top: 4px; letter-spacing: 0.3px; }
        
        /* === GRIDS === */
        .grid { display: grid; gap: 16px; }
        .grid-2 { grid-template-columns: 1fr 1fr; }
        .grid-3 { grid-template-columns: 1fr 1fr 1fr; }
        .grid-5 { grid-template-columns: repeat(5, 1fr); }
        
        /* === STRATEGIC INSIGHTS === */
        .insight {
          background: #f0fdf4; border-left: 3px solid #10b981;
          padding: 14px 16px; margin-bottom: 12px; border-radius: 0 5px 5px 0;
        }
        .insight.blue { background: #eff6ff; border-left-color: #3b82f6; }
        .insight.amber { background: #fffbeb; border-left-color: #f59e0b; }
        .insight.neutral { background: #f3f4f6; border-left-color: #6b7280; }
        .insight-title { font-size: 10px; font-weight: 600; color: #1f2937; margin-bottom: 4px; }
        .insight-text { font-size: 9.5px; color: #4b5563; line-height: 1.6; }
        
        /* === TABLES === */
        .table { width: 100%; border-collapse: collapse; font-size: 9px; }
        .table th, .table td { padding: 10px 12px; text-align: left; border-bottom: 1px solid #e5e7eb; }
        .table th { 
          background: #f9fafb; font-weight: 600; text-transform: uppercase; 
          font-size: 8px; letter-spacing: 0.5px; color: #6b7280;
        }
        .table tbody tr:last-child td { border-bottom: none; }
        
        /* === BADGES === */
        .badge {
          display: inline-block; padding: 3px 8px; border-radius: 3px;
          font-size: 8px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;
        }
        .badge-verified { background: #d1fae5; color: #065f46; }
        .badge-estimated { background: #fef3c7; color: #92400e; }
        .badge-demo { background: #e5e7eb; color: #4b5563; }
        
        /* === CHART CONTAINER === */
        .chart-container {
          background: #fafafa; border-radius: 6px; padding: 16px; margin-bottom: 16px;
        }
        .chart-title { font-size: 10px; font-weight: 600; color: #374151; margin-bottom: 12px; }
        
        /* === FOOTNOTES === */
        .footnote { font-size: 8px; color: #9ca3af; margin-top: 12px; line-height: 1.5; }
        .footnote sup { color: #6b7280; }
        
        /* === DISCLAIMER === */
        .disclaimer {
          background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 5px;
          padding: 16px; font-size: 8.5px; color: #6b7280; line-height: 1.7;
        }
        .disclaimer strong { color: #4b5563; }
        
        /* === SUMMARY BOX === */
        .summary-box {
          background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
          border-radius: 6px; padding: 18px; text-align: center;
        }
        .summary-label { font-size: 9px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
        .summary-value { font-size: 26px; font-weight: 700; color: #065f46; }
        .summary-note { font-size: 9px; color: #6b7280; margin-top: 6px; }
        
        /* === PRINT === */
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .cover { background: #111827 !important; }
          .page { page-break-inside: avoid; }
        }
      </style>
    </head>
    <body>
      <div class="pdf-watermark">CONFIDENTIAL — US SOCCER PILOT</div>
      <!-- COVER PAGE -->
      <div class="cover">
        <div class="cover-logo">YBV</div>
        <h1 class="cover-title">US Soccer Federation<br/>Strategic Brand Intelligence Assessment</h1>
        <p class="cover-subtitle">USMNT + USWNT | Four-Year World Cup Cycle Analysis</p>
        <div class="cover-badge">CONFIDENTIAL</div>
        <div class="cover-meta">
          <div>Assessment Date: ${dateStr}</div>
          <div style="margin-top: 4px;">Report Generated: ${timeStr}</div>
        </div>
        <div class="cover-footer">YBV Brand Intelligence Platform | ISO 10668 Aligned Methodology</div>
      </div>
      
      <!-- PAGE 1: EXECUTIVE SUMMARY -->
      <div class="page">
        <div class="page-header">
          <div class="page-logo"><span>YBV</span> Intelligence</div>
          <div class="page-title">Executive Summary</div>
        </div>
        
        <div class="section">
          <div class="section-title">Estimated Brand Value Range</div>
          <div class="grid grid-2" style="margin-bottom: 20px;">
            <div class="team-card">
              <div class="team-name">U.S. Men's National Team</div>
              <div class="team-meta">FIFA Ranking: #${usmnt?.fifa_ranking || 11} | ${usmntCycle.host_status || 'CONCACAF'}</div>
              <div class="team-value">${formatCurrency(usmntEco.range_low)} – ${formatCurrency(usmntEco.range_high)}</div>
              <div class="team-label">Estimated Brand Value Range</div>
              <div style="margin-top: 14px; display: flex; gap: 20px; align-items: center;">
                <div>
                  <span style="font-size: 16px; font-weight: 700; color: #1f2937;">${usmntBrand.overall_brand_score || 0}</span>
                  <span style="font-size: 9px; color: #6b7280;"> Brand Index</span>
                </div>
                <div style="color: #059669; font-weight: 600; font-size: 11px;">+${usmntYoY}% YoY</div>
              </div>
            </div>
            <div class="team-card secondary">
              <div class="team-name">U.S. Women's National Team</div>
              <div class="team-meta">FIFA Ranking: #${uswnt?.fifa_ranking || 1} | Reigning Olympic Champions</div>
              <div class="team-value">${formatCurrency(uswntEco.range_low)} – ${formatCurrency(uswntEco.range_high)}</div>
              <div class="team-label">Estimated Brand Value Range</div>
              <div style="margin-top: 14px; display: flex; gap: 20px; align-items: center;">
                <div>
                  <span style="font-size: 16px; font-weight: 700; color: #1f2937;">${uswntBrand.overall_brand_score || 0}</span>
                  <span style="font-size: 9px; color: #6b7280;"> Brand Index</span>
                </div>
                <div style="color: #3b82f6; font-weight: 600; font-size: 11px;">+${uswntYoY}% YoY</div>
              </div>
            </div>
          </div>
          
          <div class="summary-box">
            <div class="summary-label">Combined Federation Brand Value</div>
            <div class="summary-value">${formatCurrency(combinedLow)} – ${formatCurrency(combinedHigh)}</div>
            <div class="summary-note">Directional strategic indicator based on weighted composite modelling</div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Strategic Interpretation</div>
          <div class="section-intro">
            The following assessment identifies key drivers, opportunities, and risks affecting the US Soccer Federation's brand portfolio heading into the 2026/2027 World Cup cycles.
          </div>
          
          <div class="insight">
            <div class="insight-title">Primary Growth Driver: Host Country Premium</div>
            <div class="insight-text">
              The +${usmntYoY}% year-over-year USMNT brand growth is predominantly attributable to host country status for FIFA World Cup 2026. Historical analysis of prior hosts (Russia 2018, Brazil 2014) indicates brand value premiums of 35-50% during the host cycle, suggesting current trajectory is consistent with expected dynamics.
            </div>
          </div>
          
          <div class="insight">
            <div class="insight-title">Upside Catalysts</div>
            <div class="insight-text">
              Tournament performance remains the highest-leverage variable. A USMNT quarterfinal or deeper run in 2026 could accelerate brand value growth by an additional 20-30%. For USWNT, defending Olympic gold and strong 2027 World Cup positioning maintains global leadership premium despite recent World Cup setback.
            </div>
          </div>
          
          <div class="insight amber">
            <div class="insight-title">Key Risk Factors</div>
            <div class="insight-text">
              USWNT brand trajectory shows deceleration post-World Cup 2023 (R16 exit). Generational transition risk as legacy players (Morgan, Rapinoe) approach career endpoints. USMNT concentration risk in European-based player pool creates scheduling and availability exposure.
            </div>
          </div>
          
          <div class="insight blue">
            <div class="insight-title">2026 Host Implications</div>
            <div class="insight-text">
              Co-host status with Canada and Mexico provides unprecedented domestic media exposure. Projected incremental value of $50-65M from merchandise revenue, broadcast rights uplift, and sponsor activation premiums. Federation commercial team should anticipate 2-3x typical sponsorship inquiry volume.
            </div>
          </div>
          
          <div class="insight neutral">
            <div class="insight-title">Digital vs Tournament Momentum Divergence</div>
            <div class="insight-text">
              Digital presence metrics show steady growth (+12% combined social reach YoY) independent of tournament results, indicating brand resilience. However, tournament performance remains the primary driver of sponsorship value—digital metrics alone do not fully translate to commercial premium.
            </div>
          </div>
        </div>
        
        <div class="page-footer">
          <span>US Soccer Federation | Strategic Brand Assessment</span>
          <span>Page 1 of 6</span>
        </div>
      </div>
      
      <!-- PAGE 2: BRAND EVOLUTION -->
      <div class="page">
        <div class="page-header">
          <div class="page-logo"><span>YBV</span> Intelligence</div>
          <div class="page-title">Brand Score Evolution</div>
        </div>
        
        <div class="section">
          <div class="section-title">USMNT: Four-Year Cycle Trajectory (2022–2026)</div>
          <div class="chart-container">
            ${generateEvolutionSVG(usmntEvolution, '#10b981', 'USMNT')}
          </div>
          <table class="table">
            <thead>
              <tr>
                <th>Year</th>
                <th>Brand Index</th>
                <th>Key Event</th>
                <th>Δ Change</th>
              </tr>
            </thead>
            <tbody>
              ${usmntEvolution.map((row, i) => {
                const prev = i > 0 ? usmntEvolution[i-1].score : row.score;
                const change = row.score - prev;
                const changeStr = i === 0 ? '—' : (change >= 0 ? `+${change}` : `${change}`);
                return `
                  <tr>
                    <td style="font-weight: 600;">${row.year}</td>
                    <td style="font-size: 12px; font-weight: 700;">${row.score}</td>
                    <td>${row.event}</td>
                    <td style="color: ${change > 0 ? '#059669' : change < 0 ? '#dc2626' : '#6b7280'}; font-weight: 500;">${changeStr}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </div>
        
        <div class="section">
          <div class="section-title">USWNT: Four-Year Cycle Trajectory (2023–2027)</div>
          <div class="chart-container">
            ${generateEvolutionSVG(uswntEvolution, '#3b82f6', 'USWNT')}
          </div>
          <table class="table">
            <thead>
              <tr>
                <th>Year</th>
                <th>Brand Index</th>
                <th>Key Event</th>
                <th>Δ Change</th>
              </tr>
            </thead>
            <tbody>
              ${uswntEvolution.map((row, i) => {
                const prev = i > 0 ? uswntEvolution[i-1].score : row.score;
                const change = row.score - prev;
                const changeStr = i === 0 ? '—' : (change >= 0 ? `+${change}` : `${change}`);
                return `
                  <tr>
                    <td style="font-weight: 600;">${row.year}</td>
                    <td style="font-size: 12px; font-weight: 700;">${row.score}</td>
                    <td>${row.event}</td>
                    <td style="color: ${change > 0 ? '#059669' : change < 0 ? '#dc2626' : '#6b7280'}; font-weight: 500;">${changeStr}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </div>
        
        <div class="section">
          <div class="section-title">Composite Brand Metrics</div>
          <div class="grid grid-2">
            <div>
              <div style="font-size: 10px; font-weight: 600; color: #10b981; margin-bottom: 10px;">USMNT</div>
              <div class="grid grid-5">
                <div class="metric-box"><div class="metric-value">${usmntBrand.overall_brand_score || 0}</div><div class="metric-label">Overall</div></div>
                <div class="metric-box"><div class="metric-value">${usmntBrand.brand_strength_index || 0}</div><div class="metric-label">Strength</div></div>
                <div class="metric-box"><div class="metric-value">${usmntBrand.commercial_attractiveness || 0}</div><div class="metric-label">Commercial</div></div>
                <div class="metric-box"><div class="metric-value">${usmntBrand.global_recognition || 0}</div><div class="metric-label">Global</div></div>
                <div class="metric-box"><div class="metric-value">${usmntBrand.tournament_performance_factor || 0}</div><div class="metric-label">Tournament</div></div>
              </div>
            </div>
            <div>
              <div style="font-size: 10px; font-weight: 600; color: #3b82f6; margin-bottom: 10px;">USWNT</div>
              <div class="grid grid-5">
                <div class="metric-box"><div class="metric-value">${uswntBrand.overall_brand_score || 0}</div><div class="metric-label">Overall</div></div>
                <div class="metric-box"><div class="metric-value">${uswntBrand.brand_strength_index || 0}</div><div class="metric-label">Strength</div></div>
                <div class="metric-box"><div class="metric-value">${uswntBrand.commercial_attractiveness || 0}</div><div class="metric-label">Commercial</div></div>
                <div class="metric-box"><div class="metric-value">${uswntBrand.global_recognition || 0}</div><div class="metric-label">Global</div></div>
                <div class="metric-box"><div class="metric-value">${uswntBrand.tournament_performance_factor || 0}</div><div class="metric-label">Tournament</div></div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="page-footer">
          <span>US Soccer Federation | Strategic Brand Assessment</span>
          <span>Page 2 of 6</span>
        </div>
      </div>
      
      <!-- PAGE 3: DIGITAL PRESENCE -->
      <div class="page">
        <div class="page-header">
          <div class="page-logo"><span>YBV</span> Intelligence</div>
          <div class="page-title">Digital Presence Analysis</div>
        </div>
        
        <div class="section">
          <div class="section-title" style="display: flex; justify-content: space-between; align-items: center;">
            <span>Verified Social Media Signals</span>
            ${isDemoMode 
              ? '<span class="badge badge-demo">Demonstration Data</span>' 
              : '<span class="badge badge-verified">Verified Data</span>'}
          </div>
          ${!isDemoMode && formattedLastUpdated ? `<div style="font-size: 9px; color: #6b7280; margin-bottom: 16px;">Last verification: ${formattedLastUpdated}</div>` : ''}
          
          <table class="table">
            <thead>
              <tr>
                <th>Platform</th>
                <th>USMNT</th>
                <th>Handle</th>
                <th>USWNT</th>
                <th>Handle</th>
                <th>Data Source</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="font-weight: 600;">Instagram</td>
                <td style="font-weight: 600; font-size: 11px;">${formatNumber(getVerifiedValue('usmnt', 'instagram').value)}</td>
                <td style="color: #6b7280;">@${getVerifiedValue('usmnt', 'instagram').handle || 'usmnt'}</td>
                <td style="font-weight: 600; font-size: 11px;">${formatNumber(getVerifiedValue('uswnt', 'instagram').value)}</td>
                <td style="color: #6b7280;">@${getVerifiedValue('uswnt', 'instagram').handle || 'uswnt'}</td>
                <td><span class="badge ${getVerifiedValue('usmnt', 'instagram').source === 'VERIFIED' ? 'badge-verified' : getVerifiedValue('usmnt', 'instagram').source === 'DEMONSTRATION' ? 'badge-demo' : 'badge-estimated'}">${getVerifiedValue('usmnt', 'instagram').source === 'VERIFIED' ? 'Verified' : getVerifiedValue('usmnt', 'instagram').source === 'DEMONSTRATION' ? 'Demo' : 'Estimated'}</span></td>
              </tr>
              <tr>
                <td style="font-weight: 600;">X (Twitter)</td>
                <td style="font-weight: 600; font-size: 11px;">${formatNumber(getVerifiedValue('usmnt', 'twitter').value)}</td>
                <td style="color: #6b7280;">@${getVerifiedValue('usmnt', 'twitter').handle || 'USMNT'}</td>
                <td style="font-weight: 600; font-size: 11px;">${formatNumber(getVerifiedValue('uswnt', 'twitter').value)}</td>
                <td style="color: #6b7280;">@${getVerifiedValue('uswnt', 'twitter').handle || 'USWNT'}</td>
                <td><span class="badge ${getVerifiedValue('usmnt', 'twitter').source === 'VERIFIED' ? 'badge-verified' : getVerifiedValue('usmnt', 'twitter').source === 'DEMONSTRATION' ? 'badge-demo' : 'badge-estimated'}">${getVerifiedValue('usmnt', 'twitter').source === 'VERIFIED' ? 'Verified' : getVerifiedValue('usmnt', 'twitter').source === 'DEMONSTRATION' ? 'Demo' : 'Estimated'}</span></td>
              </tr>
            </tbody>
          </table>
          
          <div class="footnote">
            <sup>1</sup> <strong>Verified</strong> = Manually validated snapshot from official public account profiles.<br/>
            <sup>2</sup> Source URLs archived for audit trail.
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Estimated Social Media Signals</div>
          <table class="table">
            <thead>
              <tr>
                <th>Platform</th>
                <th>USMNT</th>
                <th>Handle</th>
                <th>USWNT</th>
                <th>Handle</th>
                <th>Data Source</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="font-weight: 600;">TikTok</td>
                <td style="font-weight: 600; font-size: 11px;">${formatNumber(getVerifiedValue('usmnt', 'tiktok').value)}</td>
                <td style="color: #6b7280;">@usmnt</td>
                <td style="font-weight: 600; font-size: 11px;">${formatNumber(getVerifiedValue('uswnt', 'tiktok').value)}</td>
                <td style="color: #6b7280;">@uswnt</td>
                <td><span class="badge badge-estimated">Estimated</span></td>
              </tr>
              <tr>
                <td style="font-weight: 600;">YouTube</td>
                <td style="font-weight: 600; font-size: 11px;">${formatNumber(getVerifiedValue('usmnt', 'youtube').value)}</td>
                <td style="color: #6b7280;">@ussoccer</td>
                <td style="font-weight: 600; font-size: 11px;">${formatNumber(getVerifiedValue('uswnt', 'youtube').value)}</td>
                <td style="color: #6b7280;">@ussoccer</td>
                <td><span class="badge badge-estimated">Estimated</span></td>
              </tr>
            </tbody>
          </table>
          
          <div class="footnote">
            <sup>3</sup> <strong>Estimated</strong> = Derived from public signal modelling and third-party analytics. Subject to signal variation.<br/>
            <sup>4</sup> YouTube subscriber count represents shared US Soccer federation channel allocation.
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Aggregate Digital Footprint</div>
          <div class="grid grid-3">
            <div class="metric-box" style="background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);">
              <div class="metric-value" style="color: #10b981;">${formatNumber(verifiedSocialData?.teams?.usmnt?.total_reach || usmntDigital.total_social_reach || 0)}</div>
              <div class="metric-label">USMNT Total Reach</div>
            </div>
            <div class="metric-box" style="background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);">
              <div class="metric-value" style="color: #3b82f6;">${formatNumber(verifiedSocialData?.teams?.uswnt?.total_reach || uswntDigital.total_social_reach || 0)}</div>
              <div class="metric-label">USWNT Total Reach</div>
            </div>
            <div class="metric-box" style="background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);">
              <div class="metric-value" style="color: #1f2937;">${formatNumber((verifiedSocialData?.combined_reach) || (usmntDigital.total_social_reach || 0) + (uswntDigital.total_social_reach || 0))}</div>
              <div class="metric-label">Combined Federation</div>
            </div>
          </div>
        </div>
        
        ${isDemoMode ? `
        <div class="disclaimer" style="margin-top: 20px;">
          <strong>Note:</strong> This report is generated in demonstration mode using simulated data for illustrative purposes. 
          Actual verified data is available when demonstration mode is disabled in the platform interface.
        </div>
        ` : ''}
        
        <div class="page-footer">
          <span>US Soccer Federation | Strategic Brand Assessment</span>
          <span>Page 3 of 6</span>
        </div>
      </div>
      
      <!-- PAGE 4: STAR PLAYERS -->
      <div class="page">
        <div class="page-header">
          <div class="page-logo"><span>YBV</span> Intelligence</div>
          <div class="page-title">Roster Brand Analysis</div>
        </div>
        
        <div class="section">
          <div class="section-title">USMNT: Top 5 Players by Brand Score</div>
          <table class="table">
            <thead>
              <tr>
                <th style="width: 30px;">#</th>
                <th>Player</th>
                <th>Social Following</th>
                <th>Brand Score</th>
                <th>Strategic Notes</th>
              </tr>
            </thead>
            <tbody>
              ${(usmntRoster.star_players || []).slice(0, 5).map((p, i) => `
                <tr>
                  <td style="font-weight: 600; color: #10b981;">${i + 1}</td>
                  <td style="font-weight: 600;">${p.name}</td>
                  <td>${formatNumber(p.instagram_followers)}</td>
                  <td style="font-size: 13px; font-weight: 700; color: #10b981;">${p.brand_score}</td>
                  <td style="font-size: 9px; color: #6b7280;">${
                    p.name === 'Christian Pulisic' ? 'Premier brand asset; AC Milan platform amplifies global reach' : 
                    p.name === 'Weston McKennie' ? 'Juventus tenure provides Serie A exposure' :
                    p.name === 'Tyler Adams' ? 'Leadership positioning; potential captain premium' :
                    p.name === 'Gio Reyna' ? 'Generational talent narrative; injury recovery variable' :
                    'Emerging profile; trajectory monitoring recommended'
                  }</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="footnote" style="margin-top: 10px;">
            Aggregate Roster Brand Power: ${usmntRoster.total_roster_brand_power || 'N/A'} | Average Player Score: ${usmntRoster.average_player_brand_score || 'N/A'}
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">USWNT: Top 5 Players by Brand Score</div>
          <table class="table">
            <thead>
              <tr>
                <th style="width: 30px;">#</th>
                <th>Player</th>
                <th>Social Following</th>
                <th>Brand Score</th>
                <th>Strategic Notes</th>
              </tr>
            </thead>
            <tbody>
              ${(uswntRoster.star_players || []).slice(0, 5).map((p, i) => `
                <tr>
                  <td style="font-weight: 600; color: #3b82f6;">${i + 1}</td>
                  <td style="font-weight: 600;">${p.name}</td>
                  <td>${formatNumber(p.instagram_followers)}</td>
                  <td style="font-size: 13px; font-weight: 700; color: #3b82f6;">${p.brand_score}</td>
                  <td style="font-size: 9px; color: #6b7280;">${
                    p.name === 'Alex Morgan' ? 'Global ambassador; crossover media presence' : 
                    p.name === 'Megan Rapinoe' ? 'Cultural icon status; retirement transition risk' :
                    p.name === 'Trinity Rodman' ? 'Next-generation flagship; growth trajectory strong' :
                    p.name === 'Sophia Smith' ? 'Olympic gold contributor; commercial potential rising' :
                    'Established contributor; steady brand positioning'
                  }</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="footnote" style="margin-top: 10px;">
            Aggregate Roster Brand Power: ${uswntRoster.total_roster_brand_power || 'N/A'} | Average Player Score: ${uswntRoster.average_player_brand_score || 'N/A'}
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Portfolio Implications</div>
          <div class="grid grid-2">
            <div class="insight">
              <div class="insight-title">USMNT Concentration Analysis</div>
              <div class="insight-text">
                Christian Pulisic represents approximately ${Math.round((78 / (usmntRoster.total_roster_brand_power || 350)) * 100)}% of identifiable roster brand value. 
                European club platform dependency creates both opportunity (global reach) and risk (scheduling, injury).
              </div>
            </div>
            <div class="insight blue">
              <div class="insight-title">USWNT Transition Planning</div>
              <div class="insight-text">
                Legacy players (Morgan, Rapinoe) comprise ${Math.round(((88 + 82) / (uswntRoster.total_roster_brand_power || 375)) * 100)}% of top-tier brand value. 
                Strategic imperative to accelerate next-generation player brand development.
              </div>
            </div>
          </div>
        </div>
        
        <div class="page-footer">
          <span>US Soccer Federation | Strategic Brand Assessment</span>
          <span>Page 4 of 6</span>
        </div>
      </div>
      
      <!-- PAGE 5: METHODOLOGY -->
      <div class="page">
        <div class="page-header">
          <div class="page-logo"><span>YBV</span> Intelligence</div>
          <div class="page-title">Methodology</div>
        </div>
        
        <div class="section">
          <div class="section-title">Valuation Framework</div>
          <div class="section-intro">
            YBV brand valuations employ a weighted composite model normalized across peer national team cohorts. 
            The methodology synthesizes publicly available data signals across five dimensions, applying volatility adjustments 
            to account for tournament-cycle variance and market condition shifts.
          </div>
          
          <table class="table">
            <thead>
              <tr>
                <th>Component</th>
                <th>Weight</th>
                <th>Description</th>
                <th>Normalization Basis</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="font-weight: 600;">Digital Presence</td>
                <td>20%</td>
                <td>Social media reach, engagement velocity, platform diversity</td>
                <td>Top-50 national teams</td>
              </tr>
              <tr>
                <td style="font-weight: 600;">Roster Brand Power</td>
                <td>25%</td>
                <td>Aggregate player brand scores, star concentration, depth</td>
                <td>Confederation peers</td>
              </tr>
              <tr>
                <td style="font-weight: 600;">Tournament Performance</td>
                <td>20%</td>
                <td>World Cup results, confederation championships, FIFA ranking</td>
                <td>Historical cycle data</td>
              </tr>
              <tr>
                <td style="font-weight: 600;">Commercial Strength</td>
                <td>20%</td>
                <td>Kit deal value, tier-1 sponsors, broadcast rights, merchandise</td>
                <td>Market comparables</td>
              </tr>
              <tr>
                <td style="font-weight: 600;">Market Factors</td>
                <td>15%</td>
                <td>Host premium, domestic market size, global exposure index</td>
                <td>Economic indicators</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div class="section">
          <div class="section-title">Framework Alignment</div>
          <div class="grid grid-3">
            <div class="metric-box">
              <div style="font-size: 11px; font-weight: 600; color: #1f2937;">ISO 10668</div>
              <div class="metric-label">Brand Valuation Standard</div>
            </div>
            <div class="metric-box">
              <div style="font-size: 11px; font-weight: 600; color: #1f2937;">BSI-Aligned</div>
              <div class="metric-label">Strength Index Model</div>
            </div>
            <div class="metric-box">
              <div style="font-size: 11px; font-weight: 600; color: #1f2937;">Interbrand-Aligned</div>
              <div class="metric-label">Valuation Methodology</div>
            </div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Signal Translation</div>
          <table class="table">
            <thead>
              <tr>
                <th>Raw Signal</th>
                <th>Derived Metric</th>
                <th>Method</th>
                <th>Example</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Instagram Followers</td>
                <td>Digital Reach Score</td>
                <td>Logarithmic normalization</td>
                <td>3.2M → 85 pts</td>
              </tr>
              <tr>
                <td>World Cup Result</td>
                <td>Tournament Factor</td>
                <td>Tiered scoring matrix</td>
                <td>R16 → +12 pts</td>
              </tr>
              <tr>
                <td>Host Country Status</td>
                <td>Market Premium</td>
                <td>Multiplier application</td>
                <td>Host → 1.4x</td>
              </tr>
              <tr>
                <td>FIFA Ranking</td>
                <td>Global Position Score</td>
                <td>Inverse ranking scale</td>
                <td>#11 → 78 pts</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div class="section">
          <div class="section-title">Data Hierarchy</div>
          <div class="grid grid-3">
            <div class="insight" style="margin-bottom: 0;">
              <div class="insight-title">Priority 1: Verified</div>
              <div class="insight-text">Manually validated snapshots from official public sources with audit trail.</div>
            </div>
            <div class="insight amber" style="margin-bottom: 0;">
              <div class="insight-title">Priority 2: Estimated</div>
              <div class="insight-text">Derived from public signal modelling when direct verification unavailable.</div>
            </div>
            <div class="insight neutral" style="margin-bottom: 0;">
              <div class="insight-title">Priority 3: Modelled</div>
              <div class="insight-text">Projected values based on historical patterns and market indicators.</div>
            </div>
          </div>
        </div>
        
        <div class="page-footer">
          <span>US Soccer Federation | Strategic Brand Assessment</span>
          <span>Page 5 of 6</span>
        </div>
      </div>
      
      <!-- PAGE 6: DISCLAIMER & APPENDIX -->
      <div class="page">
        <div class="page-header">
          <div class="page-logo"><span>YBV</span> Intelligence</div>
          <div class="page-title">Disclaimer & Appendix</div>
        </div>
        
        <div class="section">
          <div class="section-title">Important Disclaimer</div>
          <div class="disclaimer">
            <p style="margin-bottom: 12px;">
              <strong>Strategic Estimation, Not Audited Valuation:</strong> The brand valuations, projections, and index scores 
              presented in this assessment are directional strategic indicators derived from publicly available data signals and 
              proprietary analytical models. They do not constitute financial advice, audited appraisals, or certified valuations.
            </p>
            <p style="margin-bottom: 12px;">
              <strong>Subject to Signal Variation:</strong> All figures are subject to variation based on data source availability, 
              market conditions, tournament outcomes, and other external factors. Historical performance does not guarantee future results.
            </p>
            <p style="margin-bottom: 12px;">
              <strong>Intended Use:</strong> This document is intended solely for internal strategic planning, commercial negotiation 
              support, and executive decision-making purposes within the authorized recipient organization. Distribution beyond 
              authorized recipients requires written consent.
            </p>
            <p>
              <strong>No Warranty:</strong> YBV makes no representation or warranty, express or implied, regarding the accuracy, 
              completeness, or reliability of the information contained herein. Users should conduct independent verification 
              and due diligence before making decisions based on this assessment.
            </p>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Appendix: Reference Endpoints</div>
          <div style="background: #f9fafb; padding: 14px; border-radius: 5px; font-family: 'SF Mono', Consolas, monospace; font-size: 9px; color: #4b5563;">
            <div style="margin-bottom: 6px;"><strong>Verified Social Data:</strong> /api/us-soccer/verified-social</div>
            <div style="margin-bottom: 6px;"><strong>Team Intelligence:</strong> /api/national-team/{team_id}</div>
            <div style="margin-bottom: 6px;"><strong>Cycle Analysis:</strong> /api/national-team/{team_id}/cycle-analysis</div>
            <div><strong>Methodology:</strong> /methodology</div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Document Information</div>
          <table class="table">
            <tbody>
              <tr>
                <td style="font-weight: 600; width: 150px;">Assessment Date</td>
                <td>${dateStr}</td>
              </tr>
              <tr>
                <td style="font-weight: 600;">Report Generated</td>
                <td>${dateStr} at ${timeStr}</td>
              </tr>
              <tr>
                <td style="font-weight: 600;">Data Mode</td>
                <td>${isDemoMode ? 'Demonstration (Simulated Data)' : 'Production (Verified Data)'}</td>
              </tr>
              <tr>
                <td style="font-weight: 600;">Verification Status</td>
                <td>${isDemoMode ? 'N/A — Demonstration Mode' : `Instagram & X verified as of ${formattedLastUpdated || 'N/A'}`}</td>
              </tr>
              <tr>
                <td style="font-weight: 600;">Classification</td>
                <td>Confidential — Internal Use Only</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div style="margin-top: 40px; text-align: center; color: #9ca3af; font-size: 9px;">
          <div style="margin-bottom: 8px;">
            <strong style="color: #10b981; font-size: 11px; letter-spacing: 2px;">YBV</strong>
          </div>
          <div>Brand Intelligence Platform</div>
          <div style="margin-top: 4px;">Strategic Analytics for Sports Organizations</div>
          <div style="margin-top: 8px; color: #d1d5db;">ISO 10668 Aligned | BSI-Aligned Methodology | Interbrand-Aligned Valuation</div>
        </div>
        
        <div class="page-footer">
          <span>US Soccer Federation | Strategic Brand Assessment</span>
          <span>Page 6 of 6</span>
        </div>
      </div>
      <div class="pdf-watermark-footer">CONFIDENTIAL — US SOCCER PILOT | Generated ${new Date().toISOString()}</div>
    </body>
    </html>
  `;
};
